import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { CheckCircle2, Lightbulb } from "lucide-react";

import api from "../../const/axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

// Keep in sync with ContactsController.FeatureRequestCategories.
const CATEGORIES = [
  { value: "feature", label: "Yeni funksiya" },
  { value: "improvement", label: "Təkmilləşdirmə" },
  { value: "other", label: "Digər" },
] as const;

const TITLE_MAX = 120;
const DESCRIPTION_MAX = 2000;

interface FeatureRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const emptyForm = { title: "", description: "", category: "feature", name: "", email: "", website: "" };

/** "Request a feature" form. Saved as a Contacts row with Topic = "feature_request". */
const FeatureRequestDialog: React.FC<FeatureRequestDialogProps> = ({ open, onOpenChange }) => {
  const user = useSelector((state: any) => state.user);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  // Start fresh each time the dialog opens; prefill the sender for logged-in users.
  useEffect(() => {
    if (!open) return;
    setSent(false);
    setErrors({});
    setForm({
      ...emptyForm,
      name: user?.isLoggedIn ? [user.data?.name, user.data?.surname].filter(Boolean).join(" ") : "",
      email: user?.isLoggedIn ? user.data?.email ?? "" : "",
    });
  }, [open, user?.isLoggedIn, user?.data]);

  const set = (field: keyof typeof emptyForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    setErrors((er) => ({ ...er, [field]: "" }));
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    if (form.title.trim().length < 3) errs.title = "Qısa başlıq yazın (ən azı 3 simvol).";
    if (form.description.trim().length < 10) errs.description = "Təklifi bir az ətraflı izah edin (ən azı 10 simvol).";
    if (form.email.trim() && !form.email.includes("@")) errs.email = "Email düzgün deyil.";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await api.post("contacts/feature-request", {
        ...form,
        title: form.title.trim(),
        description: form.description.trim(),
        pageUrl: window.location.pathname + window.location.search,
      });
      setSent(true);
    } catch (err: any) {
      setErrors({ form: err?.response?.data?.message ?? "Göndərmək mümkün olmadı. Bir az sonra yenidən cəhd edin." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        {sent ? (
          <div className="py-4 text-center space-y-3">
            <div className="w-14 h-14 rounded-full bg-green-50 dark:bg-green-950 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-7 h-7 text-green-500" />
            </div>
            <h2 className="text-lg font-black text-gray-900 dark:text-gray-50">Təşəkkür edirik!</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">Təklifiniz komandamıza çatdı.</p>
            <Button variant="outline" className="w-full" onClick={() => onOpenChange(false)}>Bağla</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-amber-500" />
                Funksiya təklif et
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Saytda nəyi görmək istərdiniz? Fikrinizi bizimlə bölüşün.
              </p>

              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, category: c.value }))}
                    aria-pressed={form.category === c.value}
                    className={cn(
                      "appearance-none !rounded-full border px-3 py-1 text-xs font-semibold transition-colors",
                      form.category === c.value
                        ? "border-amber-400 bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300"
                        : "border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                    )}
                  >
                    {c.label}
                  </button>
                ))}
              </div>

              <Field label="Başlıq" error={errors.title}>
                <Input
                  value={form.title}
                  onChange={set("title")}
                  maxLength={TITLE_MAX}
                  placeholder="Məs.: Paketləri PDF kimi yükləmək"
                />
              </Field>

              <Field label="Təsvir" error={errors.description}>
                <textarea
                  value={form.description}
                  onChange={set("description")}
                  maxLength={DESCRIPTION_MAX}
                  rows={5}
                  placeholder="Nə etmək istəyirsiniz və bu sizə necə kömək edər?"
                  className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-800 dark:text-gray-100 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-transparent placeholder:text-gray-400 dark:placeholder:text-gray-500"
                />
                <p className="text-right text-[11px] text-gray-400">{form.description.length}/{DESCRIPTION_MAX}</p>
              </Field>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Adınız (istəyə bağlı)">
                  <Input value={form.name} onChange={set("name")} maxLength={100} />
                </Field>
                <Field label="Email (istəyə bağlı)" error={errors.email}>
                  <Input type="email" value={form.email} onChange={set("email")} maxLength={254} />
                </Field>
              </div>

              {/* Honeypot — hidden from people, bots tend to fill it in */}
              <input
                type="text"
                name="website"
                value={form.website}
                onChange={set("website")}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="hidden"
              />

              {errors.form && <p className="text-red-500 text-xs">{errors.form}</p>}

              <Button type="submit" disabled={loading} className="w-full">
                {loading ? "Göndərilir..." : "Göndər"}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Field: React.FC<{ label: string; error?: string; children: React.ReactNode }> = ({ label, error, children }) => (
  <div className="space-y-1.5">
    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200 block">{label}</label>
    {children}
    {error && <p className="text-red-500 text-xs">{error}</p>}
  </div>
);

export default FeatureRequestDialog;
